import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, shareReplay, throwError } from 'rxjs';
import { ENV } from '@bodyanalytics/app-config';
import { CustomerAccountProfileI } from '@bodyanalytics/data-models-ui';
import { ApiLocalRoutesEnum } from '../../enums/api-routes.enum';

/**
 * Customer identity probe for the customer-portal route guard (#840 / T-28).
 *
 * `verify()` hits the JWT-guarded, identity-from-token customer-self profile
 * endpoint (`GET /api/v1/customer-self-service-api/profile`). The Auth0 HTTP
 * interceptor attaches the access token, and the backend resolves the caller's
 * OWN Customer row from the token email (no client-supplied id). It emits the
 * profile on success and rethrows the FULL `HttpErrorResponse` (status + parsed
 * body) on failure, so the guard can key its decision on both the status AND the
 * tagged error code in the body:
 *   - 200 → a Customer row exists for this Auth0 identity        (guard allows)
 *   - 404 + `code: CUSTOMER_NOT_FOUND` → no Customer row yet      (guard → /register/account)
 *   - 404 without that code (stale/misrouted route) → untrusted   (guard → /error, fail closed)
 *   - 401 → token carries no usable email claim                  (guard → re-login)
 *
 * Caching: a SUCCESSFUL probe is memoised for the lifetime of the SPA session, so
 * navigating between guarded routes costs one network call per page load, not one
 * per navigation. Failures are NEVER memoised — `catchError` drops the cache and
 * rethrows — so a 404 cannot lock a user out: once they finish registration,
 * `RegisterInitComponent` calls `auth.loginWithRedirect()` (a full page reload
 * that recreates this root singleton), and even within a single SPA session the
 * next probe re-runs against the now-existing Customer row.
 */
@Injectable({ providedIn: 'root' })
export class CustomerIdentityService {
  private readonly env = inject(ENV);
  private readonly http = inject(HttpClient);

  private readonly profileUrl = `${this.env.api}/${ApiLocalRoutesEnum.API_SUFFIX_CUSTOMER_SELF_PROFILE}`;

  private cached$?: Observable<CustomerAccountProfileI>;

  /**
   * Probes the backend for the caller's own Customer row. Emits the profile on
   * success; errors with the HttpErrorResponse (404 = no Customer, 401 = no
   * identity on the token) on failure. See the class doc for status semantics.
   */
  verify(): Observable<CustomerAccountProfileI> {
    if (!this.cached$) {
      // Order matters: shareReplay is INSIDE catchError so the replay buffer only
      // ever holds a SUCCESS. catchError is the outermost operator, so on failure
      // it drops the memo (next call re-probes) and rethrows — a 404 is never
      // cached and so can never lock a user out.
      this.cached$ = this.http.get<CustomerAccountProfileI>(this.profileUrl).pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
        catchError(err => {
          this.cached$ = undefined;
          return throwError(() => err);
        }),
      );
    }
    return this.cached$;
  }

  /** Drops the memoised identity (e.g. on logout). */
  clear(): void {
    this.cached$ = undefined;
  }
}
